/**
 * TaskApprovalActions — botões inline [Aprovar][Rejeitar] em notification
 * tipo 'task_approval_request' (UI-2 / TD-V2-PERM-06).
 *
 * Renderizado dentro do <li> da NotificationsBell quando type=task_approval_request
 * e payload tem approvalId. Após decisão, dispara API + invalida notifications.
 *
 * 409 (already-decided) tratado com mensagem amigável.
 */

import { useState } from 'react';

import { Button } from '@/components/ui';
import { api, ApiError } from '@/lib/api';

interface TaskApprovalActionsProps {
  approvalId: string;
  onDecided: () => void;
}

export function TaskApprovalActions({ approvalId, onDecided }: TaskApprovalActionsProps) {
  const [pending, setPending] = useState<'approve' | 'reject' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showRejectInput, setShowRejectInput] = useState(false);
  const [razao, setRazao] = useState('');

  async function handle(decision: 'approve' | 'reject') {
    setPending(decision);
    setError(null);
    try {
      await api.post(`/api/v1/task-approvals/${encodeURIComponent(approvalId)}/decide`, {
        decision,
        ...(decision === 'reject' && razao ? { razao } : {}),
      });
      onDecided();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setError('Esta aprovação já foi decidida por outro admin.');
        onDecided(); // refresh anyway pra ver estado atualizado
      } else if (err instanceof ApiError && err.status === 503) {
        setError('Feature de aprovações desativada. Pergunte ao admin.');
      } else {
        setError(err instanceof Error ? err.message : 'Erro ao processar decisão');
      }
    } finally {
      setPending(null);
    }
  }

  return (
    <div
      data-testid={`approval-actions-${approvalId}`}
      className="flex flex-col gap-1.5 mt-2"
      onClick={(e) => e.stopPropagation()}
    >
      {showRejectInput && (
        <input
          type="text"
          value={razao}
          onChange={(e) => setRazao(e.target.value.slice(0, 200))}
          placeholder="Razão (opcional, max 200 chars)"
          data-testid={`approval-razao-${approvalId}`}
          className="text-xs w-full px-2 py-1 rounded border border-line dark:border-line-dark bg-paper dark:bg-paper-dark"
        />
      )}
      <div className="flex gap-1.5">
        <Button
          type="button"
          size="sm"
          variant="primary"
          onClick={() => handle('approve')}
          loading={pending === 'approve'}
          disabled={pending !== null}
          data-testid={`approval-approve-${approvalId}`}
        >
          ✓ Aprovar
        </Button>
        <Button
          type="button"
          size="sm"
          variant="destructive"
          onClick={() => {
            if (!showRejectInput) {
              setShowRejectInput(true);
            } else {
              void handle('reject');
            }
          }}
          loading={pending === 'reject'}
          disabled={pending !== null}
          data-testid={`approval-reject-${approvalId}`}
        >
          ✕ Rejeitar
        </Button>
      </div>
      {error && (
        <p
          data-testid={`approval-error-${approvalId}`}
          className="text-xs text-danger"
        >
          {error}
        </p>
      )}
    </div>
  );
}
